import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MessageCircle, ArrowRight, Check, CheckCircle2, ArrowLeft } from "lucide-react";
import { serviceDetailMeta, richServiceIcons } from "../data/mock";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTASection from "../components/CTASection";
import { useLanguage } from "../i18n/LanguageContext";
import { usePublicContent } from "../i18n/PublicContentContext";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const pub = usePublicContent();
  const meta = serviceDetailMeta[slug];
  const data = t.serviceDetail[slug];
  const ui = t.sdUI;

  const richText = t.richServiceDetail?.[slug];
  const richIcons = richServiceIcons[slug];

  const aiOverride = React.useMemo(() => {
    const v = pub.ai_concierge;
    if (slug !== "whatsapp-automation" || !v || !v.heading_en) return null;
    const L = (en, hi) => (lang === "hi" ? (v[hi] || v[en] || "") : (v[en] || ""));
    return {
      eyebrow: L("eyebrow_en", "eyebrow_hi"),
      heading: L("heading_en", "heading_hi"),
      intro: L("intro_en", "intro_hi"),
      points: (v.points || []).map((p) => ({
        title: lang === "hi" ? (p.title_hi || p.title_en) : p.title_en,
        desc: lang === "hi" ? (p.desc_hi || p.desc_en) : p.desc_en,
      })),
      footer: L("footer_en", "footer_hi"),
    };
  }, [pub.ai_concierge, slug, lang]);

  const rich = richText
    ? {
        ...richText,
        aiConcierge: aiOverride || richText.aiConcierge,
        features: richText.features.map((fe, i) => ({ ...fe, icon: richIcons[i] })),
        heroButtons: richText.heroButtons || [
          { label: ui.getStarted, type: "primary", action: "contact" },
          { label: ui.talkExpert, type: "outline", action: "whatsapp" },
        ],
      }
    : null;

  if (!meta || (!data && !rich)) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center px-5">
        <div className="text-center">
          <h1 className="font-head text-3xl font-bold text-[#12284B]">{ui.notFound}</h1>
          <Link
            to="/services"
            className="mt-6 inline-flex items-center gap-2 bg-[#E84B1C] text-white font-head font-semibold px-6 py-3 rounded-full"
          >
            <ArrowLeft size={18} /> {ui.back}
          </Link>
        </div>
      </div>
    );
  }

  const Icon = meta.icon;
  const title = t.services[slug].title;
  const waMsg = `${ui.waLead} ${title}${ui.waTail}`;

  // Per-service DB overrides (heading, body, features) with fallback to defaults
  const sdOv = (pub.services_meta || []).find((x) => x.slug === slug) || {};
  const Lp = (en, hi, def) => {
    const v = lang === "hi" ? (sdOv[hi] || sdOv[en]) : sdOv[en];
    return v && String(v).trim() ? v : def;
  };
  const headingOv = Lp("heading_en", "heading_hi", title);
  const featuresRaw = lang === "hi" ? (sdOv.features_hi || sdOv.features_en) : sdOv.features_en;
  const featuresOv = featuresRaw && String(featuresRaw).trim()
    ? String(featuresRaw).split("\n").map((x) => x.trim()).filter(Boolean)
    : null;

  // ---------- RICH LAYOUT (Channel Management, Room Partition & Pricing) ----------
  if (rich) {
    const heroButtons = rich.heroButtons;
    const richBody = Lp("body_en", "body_hi", rich.body);
    const featCards = featuresOv
      ? featuresOv.map((line, i) => ({ title: line, desc: rich.features[i]?.desc || "", icon: rich.features[i]?.icon || richIcons[i % richIcons.length] }))
      : rich.features;
    return (
      <div className="bg-white">
        <Helmet>
          <title>{`${title} | Superhost Cloud`}</title>
          <meta name="description" content={rich.subtitle} />
        </Helmet>

        {/* Hero */}
        <section className="relative pt-32 pb-20 lg:pb-24">
          <div className="absolute inset-0">
            <img src={meta.hero} alt={title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/65" />
          </div>
          <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal className="max-w-3xl">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-black/60 hover:text-[#E84B1C] transition-colors text-sm mb-6"
              >
                <ArrowLeft size={16} /> {ui.allServices}
              </Link>
              <div className="h-16 w-16 rounded-2xl bg-[#E84B1C] flex items-center justify-center mb-6 glow-orange">
                <Icon size={30} className="text-white" />
              </div>
              <h1 className="font-head text-4xl md:text-6xl font-extrabold text-[#12284B] leading-tight">{headingOv}</h1>
              <p className="mt-4 text-xl text-[#E84B1C] font-head font-semibold">{rich.subtitle}</p>
              <p className="mt-5 text-black/70 text-lg leading-relaxed max-w-2xl">{richBody}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                {heroButtons.map((b) => {
                  const cls =
                    b.type === "primary"
                      ? "inline-flex items-center justify-center gap-2 bg-[#E84B1C] hover:bg-[#F0673E] text-white font-head font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:scale-105 glow-orange"
                      : "inline-flex items-center justify-center gap-2 border border-[#12284B]/25 hover:border-[#E84B1C] text-[#12284B] font-head font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:bg-[#12284B]/5";
                  return b.action === "whatsapp" ? (
                    <a key={b.label} href={pub.waLink(waMsg)} target="_blank" rel="noreferrer" className={cls}>
                      <MessageCircle size={18} /> {b.label}
                    </a>
                  ) : (
                    <Link key={b.label} to="/contact" className={cls}>
                      {b.label} <ArrowRight size={18} />
                    </Link>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </section>

        {/* AI Concierge (optional) */}
        {rich.aiConcierge && (
          <section className="py-8 lg:py-12">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
              <Reveal>
                <div className="rounded-2xl bg-[#17205C] border border-white/10 p-8 lg:p-10 shadow-sm">
                  <span className="inline-flex items-center gap-2 bg-[#E84B1C]/15 border border-[#E84B1C]/30 rounded-full px-4 py-1.5 text-[#E84B1C] font-head font-semibold text-sm">
                    <MessageCircle size={16} /> {rich.aiConcierge.eyebrow}
                  </span>
                  <h2 className="mt-5 font-head text-3xl md:text-4xl font-bold text-white leading-tight">
                    {rich.aiConcierge.heading}
                  </h2>
                  <p className="mt-4 text-white/70 text-lg leading-relaxed max-w-3xl">
                    {rich.aiConcierge.intro}
                  </p>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {rich.aiConcierge.points.map((p) => (
                      <div key={p.title} className="flex gap-3">
                        <CheckCircle2 size={22} className="text-[#E84B1C] shrink-0 mt-0.5" />
                        <div>
                          <div className="font-head font-semibold text-white">{p.title}</div>
                          <div className="mt-1 text-white/70 text-sm leading-relaxed">{p.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-8 font-head text-lg font-semibold text-white">
                    {rich.aiConcierge.footer}
                  </p>
                </div>
              </Reveal>
            </div>
          </section>
        )}

        {/* Features */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            {(rich.featuresHeading || rich.featuresIntro) && (
              <Reveal className="max-w-3xl mb-12">
                {rich.featuresHeading && (
                  <h2 className="font-head text-3xl md:text-4xl font-bold text-[#12284B] leading-tight">
                    {rich.featuresHeading}
                  </h2>
                )}
                {rich.featuresIntro && (
                  <p className="mt-4 text-black/70 text-lg leading-relaxed">{rich.featuresIntro}</p>
                )}
              </Reveal>
            )}
            <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featCards.map((fe) => {
                const FIcon = fe.icon;
                return (
                  <StaggerItem key={fe.title}>
                    <div className="h-full bg-[#17205C] border border-white/10 rounded-2xl p-7 shadow-sm hover:border-[#E84B1C]/50 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-[#12284B]/20">
                      <div className="h-14 w-14 rounded-xl bg-[#E84B1C]/20 flex items-center justify-center">
                        <FIcon size={26} className="text-[#E84B1C]" />
                      </div>
                      <h3 className="mt-5 font-head text-xl font-semibold text-white">{fe.title}</h3>
                      {fe.desc && <p className="mt-2.5 text-white/70 text-sm leading-relaxed">{fe.desc}</p>}
                    </div>
                  </StaggerItem>
                );
              })}
            </Stagger>
          </div>
        </section>

        {/* Steps */}
        <section className="py-16 lg:py-24 bg-[#F4F6FA] border-y border-[#12284B]/10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal className="text-center max-w-2xl mx-auto mb-14">
              <h2 className="font-head text-3xl md:text-4xl font-bold text-[#12284B] leading-tight">
                {rich.stepsHeading}
              </h2>
            </Reveal>
            <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {rich.steps.map((st) => (
                <StaggerItem key={st.no}>
                  <div className="h-full bg-white border border-[#12284B]/10 rounded-2xl p-7 shadow-sm">
                    <div className="font-head text-4xl font-extrabold text-[#E84B1C]/30">{st.no}</div>
                    <h3 className="mt-3 font-head text-lg font-semibold text-[#12284B]">{st.title}</h3>
                    <p className="mt-2 text-black/65 text-sm leading-relaxed">{st.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* Mid tagline */}
        {(rich.midTagline || rich.midText) && (
          <section className="pb-4 lg:pb-8">
            <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
              <Reveal>
                {rich.midTagline && (
                  <h2 className="font-head text-2xl md:text-3xl font-bold text-[#12284B]">{rich.midTagline}</h2>
                )}
                {rich.midText && (
                  <p className="mt-4 text-black/70 text-lg leading-relaxed">{rich.midText}</p>
                )}
              </Reveal>
            </div>
          </section>
        )}

        {/* Why */}
        {rich.whyHeading && (
          <section className="py-16 lg:py-20">
            <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
              <Reveal>
                <h2 className="font-head text-3xl md:text-4xl font-bold text-[#12284B] leading-tight">{rich.whyHeading}</h2>
                {rich.whySub && (
                  <p className="mt-4 text-xl font-head font-semibold text-[#E84B1C]">{rich.whySub}</p>
                )}
                {rich.whyText && (
                  <p className="mt-5 text-black/70 text-lg leading-relaxed">{rich.whyText}</p>
                )}
                {rich.whyTagline && (
                  <p className="mt-6 text-2xl font-head font-bold text-[#12284B]">{rich.whyTagline}</p>
                )}
              </Reveal>
            </div>
          </section>
        )}

        {/* Bullets */}
        {rich.bullets && (
          <section className="pb-16 lg:pb-20">
            <div className="max-w-4xl mx-auto px-5 lg:px-8">
              <div className="bg-[#17205C] border border-white/10 rounded-2xl p-8">
                {rich.bulletsHeading && (
                  <h3 className="font-head text-2xl font-bold text-white mb-5">{rich.bulletsHeading}</h3>
                )}
                {rich.bulletsIntro && (
                  <p className="font-head font-semibold text-white mb-5">{rich.bulletsIntro}</p>
                )}
                <Stagger className="grid sm:grid-cols-2 gap-4">
                  {rich.bullets.map((b) => (
                    <StaggerItem key={b}>
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 h-6 w-6 rounded-full bg-[#E84B1C] flex items-center justify-center shrink-0">
                          <Check size={15} className="text-white" />
                        </span>
                        <span className="text-white/90 leading-relaxed">{b}</span>
                      </div>
                    </StaggerItem>
                  ))}
                </Stagger>
              </div>
            </div>
          </section>
        )}

        {/* Built for */}
        {rich.builtHeading && (
          <section className="pb-16 lg:pb-20">
            <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
              <Reveal>
                <h3 className="font-head text-2xl md:text-3xl font-bold text-[#12284B]">{rich.builtHeading}</h3>
                <p className="mt-4 text-black/70 text-lg leading-relaxed">{rich.builtText}</p>
              </Reveal>
            </div>
          </section>
        )}

        {/* Post tagline */}
        {rich.postTagline && (
          <section className="pb-16 lg:pb-20">
            <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
              <Reveal>
                <p className="text-2xl md:text-3xl font-head font-bold text-[#12284B]">{rich.postTagline}</p>
              </Reveal>
            </div>
          </section>
        )}

        <CTASection
          title={rich.closingHeading || t.cta.homeTitle}
          subtitle={rich.closingText || t.cta.homeSubtitle}
          waMessage={waMsg}
        />
      </div>
    );
  }

  // ---------- STANDARD LAYOUT (OTA, WhatsApp) ----------
  return (
    <div className="bg-white">
      <Helmet>
        <title>{`${title} | Superhost Cloud`}</title>
        <meta name="description" content={data.tagline} />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pb-24">
        <div className="absolute inset-0">
          <img src={meta.hero} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/65" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-3xl">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-black/60 hover:text-[#E84B1C] transition-colors text-sm mb-6"
            >
              <ArrowLeft size={16} /> {ui.allServices}
            </Link>
            <div className="h-16 w-16 rounded-2xl bg-[#E84B1C] flex items-center justify-center mb-6 glow-orange">
              <Icon size={30} className="text-white" />
            </div>
            <h1 className="font-head text-4xl md:text-6xl font-extrabold text-[#12284B] leading-tight">{headingOv}</h1>
            <p className="mt-4 text-xl text-[#E84B1C] font-head font-semibold">{data.tagline}</p>
            <a
              href={pub.waLink(waMsg)}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 bg-[#E84B1C] hover:bg-[#F0673E] text-white font-head font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:scale-105 glow-orange"
            >
              <MessageCircle size={20} /> {ui.talkWhatsapp}
            </a>
          </Reveal>
        </div>
      </section>

      {/* Intro + benefits */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{ui.overview}</span>
            <h2 className="mt-3 font-head text-3xl md:text-4xl font-bold text-[#12284B] leading-tight">{ui.whatItDoes}</h2>
            <p className="mt-5 text-black/70 text-lg leading-relaxed">{Lp("body_en", "body_hi", data.intro)}</p>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {data.points.map((p) => (
                <div key={p.h} className="bg-[#F4F6FA] border border-[#12284B]/10 rounded-xl p-5">
                  <h4 className="font-head font-semibold text-[#E84B1C]">{p.h}</h4>
                  <p className="mt-1.5 text-black/65 text-sm leading-relaxed">{p.p}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="bg-[#F4F6FA] border border-[#12284B]/10 rounded-2xl p-8">
              <h3 className="font-head text-2xl font-bold text-[#12284B]">{ui.benefitsTitle}</h3>
              <Stagger className="mt-6 space-y-4">
                {(featuresOv || data.benefits).map((b) => (
                  <StaggerItem key={b}>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 h-6 w-6 rounded-full bg-[#E84B1C] flex items-center justify-center shrink-0">
                        <Check size={15} className="text-white" />
                      </span>
                      <span className="text-[#12284B] leading-relaxed">{b}</span>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 text-[#E84B1C] font-head font-semibold hover:underline"
              >
                {ui.getFreeConsult} <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection waMessage={waMsg} />
    </div>
  );
}
